import { lastValueFrom } from 'rxjs';

import {
  addBoxProduct, addBoxProductError, addBoxProductSuccess,
  deleteBox, deleteBoxError, deleteBoxSuccess,
  getBox, getBoxError, getBoxSuccess,
  showAddBoxProductSuccess, hideAddBoxProductSuccess,
  createWineBoxFromPush,
  replaceCurrentWithWineBoxFromPush,
  resetBoxQty
} from '../slices/box.slice';

import { openWineboxModal, showConfirmModal } from '../slices/gui.slice';
import { updateQty } from '@/utils/millesimaUtils';
import { getItemListToAdd } from '@/utils/wineboxUtils';
import { AppListenerEntry } from '@/redux';
import { CartModel } from '@/models';
import { STORE_ID_DEFAULT } from '@/constants/stores';
import { WINEBOX_MAX } from '@/constants/cart';

const HIDE_DELAY = 1500;

export const epicGetBox: AppListenerEntry = {
  actionCreator: getBox,
  effect: async (_action: ReturnType<typeof getBox>, { getState, dispatch, extra: { boxService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    try {
      const content = await lastValueFrom(boxService.get(storeId)) as CartModel;
      dispatch(getBoxSuccess({ content }));
    } catch (error) {
      dispatch(getBoxError({ error }));
    }
  }
};

export const epicAddBoxProduct: AppListenerEntry = {
  actionCreator: addBoxProduct,
  effect: async ({ payload }: ReturnType<typeof addBoxProduct>, { getState, dispatch, extra: { boxService } }) => {
    const { root, box } = getState();
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    const { item, qty, options } = payload;
    const setQty = (options && options.set === false) ? false : true;
    const showConfirm = (options && options.showConfirm === false) ? false : true;
    // When setQty is false, qty have do be updated if the product already exist in the current box
    const newQuantity = setQty ? qty : updateQty(qty, getItemListToAdd, box.content.products);
    const firstElem = box.content.products.length > 0 ? false : true;
    try {
      const content = await lastValueFrom(boxService.addProduct(storeId, item, newQuantity)) as any;
      const isAdd = box.content.nbItem < content.nbItem;
      const isFull = content.nbItem === WINEBOX_MAX;

      dispatch(addBoxProductSuccess({ content, showConfirm, firstElem }));
      if (isAdd && !isFull) {
        dispatch(showAddBoxProductSuccess());
      }
    } catch (error) {
      dispatch(addBoxProductError({ error }));
    }
  }
};

export const epicAddBoxProductSuccess: AppListenerEntry = {
  actionCreator: addBoxProductSuccess,
  effect: async (_action: ReturnType<typeof addBoxProductSuccess>, { getState, dispatch }) => {
    const { box } = getState();
    if (box.content.nbItem === WINEBOX_MAX || box.status.firstElem) {
      dispatch(openWineboxModal());
    }
  }
};

export const epicCreateWineBoxFromPush: AppListenerEntry = {
  actionCreator: createWineBoxFromPush,
  effect: async ({ payload }: ReturnType<typeof createWineBoxFromPush>, { getState, dispatch, extra: { boxService } }) => {
    const { root, box } = getState();
    const { itemsList, t } = payload;
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    const firstElem = box.content.products.length > 0 ? false : true;
    try {
      const newBox = await lastValueFrom(boxService.addProducts(storeId, itemsList)) as any;
      dispatch(addBoxProductSuccess({ content: newBox.wineBox, firstElem }));
      if (newBox.productsError.length > 0) {
        dispatch(showConfirmModal({
          content: {
            title: t('product.box.modalErrorPushTitle'),
            content: newBox.productsError.join(', ') + '<br/><br/>' + t('product.box.modalErrorPushContent').italics(),
            confirm: t('btn.continue')
          }
        }));
      }
    } catch (error) {
      dispatch(addBoxProductError({ error }));
    }
  }
};

export const epicReplaceCurrentWithWineBoxFromPush: AppListenerEntry = {
  actionCreator: replaceCurrentWithWineBoxFromPush,
  effect: ({ payload }: ReturnType<typeof replaceCurrentWithWineBoxFromPush>, { dispatch }) => {
    dispatch(deleteBox({ wineBoxPush: true, t: payload.t }));
  }
};

export const epicshowAddBoxProductSuccess: AppListenerEntry = {
  actionCreator: showAddBoxProductSuccess,
  effect: async (_action: ReturnType<typeof showAddBoxProductSuccess>, { dispatch, delay }) => {
    await delay(HIDE_DELAY);
    dispatch(hideAddBoxProductSuccess());
  }
};

export const epicDeleteBox: AppListenerEntry = {
  actionCreator: deleteBox,
  effect: async ({ payload }: ReturnType<typeof deleteBox>, { getState, dispatch, extra: { boxService } }) => {
    const { root, box } = getState();
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    const { wineBoxPush, t } = payload;
    const wineboxId = box.content.id;
    try {
      await lastValueFrom(boxService.delete(storeId, wineboxId));
      dispatch(deleteBoxSuccess());
      if (wineBoxPush) {
        dispatch(createWineBoxFromPush({ itemsList: getItemListToAdd(box.wineBoxPush), t }));
      }
    } catch (error) {
      dispatch(deleteBoxError({ error }));
    }
  }
};

export const epicResetBoxOnError: AppListenerEntry = {
  actionCreator: addBoxProductError,
  effect: (_action: ReturnType<typeof addBoxProductError>, { dispatch }) => {
    dispatch(resetBoxQty());
  }
};
